import * as i0 from '@angular/core';
import { inject, ElementRef, NgZone, ChangeDetectorRef, effect, DestroyRef, signal, EventEmitter, Injector, Directive, Input, Output } from '@angular/core';
import { DragGesture, HoverGesture, MoveGesture, PinchGesture, ScrollGesture, WheelGesture } from '@use-gesture/vanilla';
import { assertInjector } from 'ngxtension/assert-injector';
import { createInjectionToken } from 'ngxtension/create-injection-token';
const [injectZonelessGesture, provideFn] = createInjectionToken(() => false);
function provideZonelessGesture() {
  return provideFn(true);
}
function createGesture(_key, gesture) {
  return function _injectGesture(handler, {
    injector,
    config = () => ({}),
    zoneless
  } = {}) {
    return assertInjector(_injectGesture, injector, () => {
      const zonelessGesture = injectZonelessGesture();
      const host = inject(ElementRef);
      const zone = inject(NgZone);
      const cdr = inject(ChangeDetectorRef);
      zoneless ??= zonelessGesture;
      const ngHandler = state => {
        return handler(Object.assign(state, {
          cdr
        }));
      };
      const gestureInstance = zoneless ? zone.runOutsideAngular(() => new gesture(host.nativeElement, ngHandler)) : new gesture(host.nativeElement, ngHandler);
      effect(() => {
        if (zoneless) {
          zone.runOutsideAngular(() => {
            // @ts-expect-error typescript knows what the config is but gestureInstance can also set config for multiple gestures
            gestureInstance.setConfig(config());
          });
        } else {
          // @ts-expect-error typescript knows what the config is but gestureInstance can also set config for multiple gestures
          gestureInstance.setConfig(config());
        }
      });
      inject(DestroyRef).onDestroy(gestureInstance.destroy.bind(gestureInstance));
    });
  };
}
const injectDrag = createGesture('drag', DragGesture);
class NgxDrag {
  constructor() {
    this.config = signal({});
    this.ngxDrag = new EventEmitter();
    this.injector = inject(Injector);
  }
  set _config(config) {
    this.config.set(config);
  }
  ngOnInit() {
    injectDrag(this.ngxDrag.emit.bind(this.ngxDrag), {
      injector: this.injector,
      zoneless: this.zoneless,
      config: this.config
    });
  }
  static {
    this.ɵfac = function NgxDrag_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxDrag)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NgxDrag,
      selectors: [["", "ngxDrag", ""]],
      inputs: {
        _config: [0, "ngxDragConfig", "_config"],
        zoneless: [0, "ngxDragZoneless", "zoneless"]
      },
      outputs: {
        ngxDrag: "ngxDrag"
      },
      standalone: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxDrag, [{
    type: Directive,
    args: [{
      selector: '[ngxDrag]',
      standalone: true
    }]
  }], null, {
    _config: [{
      type: Input,
      args: ['ngxDragConfig']
    }],
    zoneless: [{
      type: Input,
      args: ['ngxDragZoneless']
    }],
    ngxDrag: [{
      type: Output
    }]
  });
})();
const injectHover = createGesture('hover', HoverGesture);
class NgxHover {
  constructor() {
    this.config = signal({});
    this.ngxHover = new EventEmitter();
    this.injector = inject(Injector);
  }
  set _config(config) {
    this.config.set(config);
  }
  ngOnInit() {
    injectHover(this.ngxHover.emit.bind(this.ngxHover), {
      injector: this.injector,
      zoneless: this.zoneless,
      config: this.config
    });
  }
  static {
    this.ɵfac = function NgxHover_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxHover)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NgxHover,
      selectors: [["", "ngxHover", ""]],
      inputs: {
        _config: [0, "ngxHoverConfig", "_config"],
        zoneless: [0, "ngxHoverZoneless", "zoneless"]
      },
      outputs: {
        ngxHover: "ngxHover"
      },
      standalone: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxHover, [{
    type: Directive,
    args: [{
      selector: '[ngxHover]',
      standalone: true
    }]
  }], null, {
    _config: [{
      type: Input,
      args: ['ngxHoverConfig']
    }],
    zoneless: [{
      type: Input,
      args: ['ngxHoverZoneless']
    }],
    ngxHover: [{
      type: Output
    }]
  });
})();
const injectMove = createGesture('move', MoveGesture);
class NgxMove {
  constructor() {
    this.config = signal({});
    this.ngxMove = new EventEmitter();
    this.injector = inject(Injector);
  }
  set _config(config) {
    this.config.set(config);
  }
  ngOnInit() {
    injectMove(this.ngxMove.emit.bind(this.ngxMove), {
      injector: this.injector,
      zoneless: this.zoneless,
      config: this.config
    });
  }
  static {
    this.ɵfac = function NgxMove_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxMove)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NgxMove,
      selectors: [["", "ngxMove", ""]],
      inputs: {
        _config: [0, "ngxMoveConfig", "_config"],
        zoneless: [0, "ngxMoveZoneless", "zoneless"]
      },
      outputs: {
        ngxMove: "ngxMove"
      },
      standalone: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxMove, [{
    type: Directive,
    args: [{
      selector: '[ngxMove]',
      standalone: true
    }]
  }], null, {
    _config: [{
      type: Input,
      args: ['ngxMoveConfig']
    }],
    zoneless: [{
      type: Input,
      args: ['ngxMoveZoneless']
    }],
    ngxMove: [{
      type: Output
    }]
  });
})();
const injectPinch = createGesture('pinch', PinchGesture);
class NgxPinch {
  constructor() {
    this.config = signal({});
    this.ngxPinch = new EventEmitter();
    this.injector = inject(Injector);
  }
  set _config(config) {
    this.config.set(config);
  }
  ngOnInit() {
    injectPinch(this.ngxPinch.emit.bind(this.ngxPinch), {
      injector: this.injector,
      zoneless: this.zoneless,
      config: this.config
    });
  }
  static {
    this.ɵfac = function NgxPinch_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxPinch)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NgxPinch,
      selectors: [["", "ngxPinch", ""]],
      inputs: {
        _config: [0, "ngxPinchConfig", "_config"],
        zoneless: [0, "ngxPinchZoneless", "zoneless"]
      },
      outputs: {
        ngxPinch: "ngxPinch"
      },
      standalone: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxPinch, [{
    type: Directive,
    args: [{
      selector: '[ngxPinch]',
      standalone: true
    }]
  }], null, {
    _config: [{
      type: Input,
      args: ['ngxPinchConfig']
    }],
    zoneless: [{
      type: Input,
      args: ['ngxPinchZoneless']
    }],
    ngxPinch: [{
      type: Output
    }]
  });
})();
const injectScroll = createGesture('scroll', ScrollGesture);
class NgxScroll {
  constructor() {
    this.config = signal({});
    this.ngxScroll = new EventEmitter();
    this.injector = inject(Injector);
  }
  set _config(config) {
    this.config.set(config);
  }
  ngOnInit() {
    injectScroll(this.ngxScroll.emit.bind(this.ngxScroll), {
      injector: this.injector,
      zoneless: this.zoneless,
      config: this.config
    });
  }
  static {
    this.ɵfac = function NgxScroll_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxScroll)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NgxScroll,
      selectors: [["", "ngxScroll", ""]],
      inputs: {
        _config: [0, "ngxScrollConfig", "_config"],
        zoneless: [0, "ngxScrollZoneless", "zoneless"]
      },
      outputs: {
        ngxScroll: "ngxScroll"
      },
      standalone: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxScroll, [{
    type: Directive,
    args: [{
      selector: '[ngxScroll]',
      standalone: true
    }]
  }], null, {
    _config: [{
      type: Input,
      args: ['ngxScrollConfig']
    }],
    zoneless: [{
      type: Input,
      args: ['ngxScrollZoneless']
    }],
    ngxScroll: [{
      type: Output
    }]
  });
})();
const injectWheel = createGesture('wheel', WheelGesture);
class NgxWheel {
  constructor() {
    this.config = signal({});
    this.ngxWheel = new EventEmitter();
    this.injector = inject(Injector);
  }
  set _config(config) {
    this.config.set(config);
  }
  ngOnInit() {
    injectWheel(this.ngxWheel.emit.bind(this.ngxWheel), {
      injector: this.injector,
      zoneless: this.zoneless,
      config: this.config
    });
  }
  static {
    this.ɵfac = function NgxWheel_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NgxWheel)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NgxWheel,
      selectors: [["", "ngxWheel", ""]],
      inputs: {
        _config: [0, "ngxWheelConfig", "_config"],
        zoneless: [0, "ngxWheelZoneless", "zoneless"]
      },
      outputs: {
        ngxWheel: "ngxWheel"
      },
      standalone: true
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgxWheel, [{
    type: Directive,
    args: [{
      selector: '[ngxWheel]',
      standalone: true
    }]
  }], null, {
    _config: [{
      type: Input,
      args: ['ngxWheelConfig']
    }],
    zoneless: [{
      type: Input,
      args: ['ngxWheelZoneless']
    }],
    ngxWheel: [{
      type: Output
    }]
  });
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgxDrag, NgxHover, NgxMove, NgxPinch, NgxScroll, NgxWheel, injectDrag, injectHover, injectMove, injectPinch, injectScroll, injectWheel, injectZonelessGesture, provideZonelessGesture };
